import * as Sentry from '@sentry/nextjs'

import { enterViewAsRequest, exitViewAsRequest } from '@/api/view-as'
import { getUserFromIdRequest } from '@/api/get-user-from-id-request'
import { forceLogout } from '@/utils/apiUtil'
import { getLandingRouteForProfile } from '@/utils/landingRouteUtil'
import {
  decode,
  getUser,
  getUserProfile,
  storeTokens,
  storeUserProfile,
  storeWorkflowSummary,
} from '@/utils/localStorageUtil'

import { localizeRoute } from '@/utils/localeRouteUtil'

const CUSTOMER_SCOPED_FILTERS = ['transactionFilterParams', 'casesFilterParams']
const TOKEN_STORAGE_KEY = 'token'

/**
 * @returns the customer the session is viewing as, or null when on the home customer
 */
export const getActingCustomerId = (): string | null => {
  try {
    return getUser()?.actingCustomerId ?? null
  } catch {
    return null
  }
}

const actingClaimOf = (storedToken: string | null): string | null => {
  try {
    return storedToken ? decode(JSON.parse(storedToken))?.actingCustomerId ?? null : null
  } catch {
    return null
  }
}

/**
 * Whether a `storage` event means another tab switched customers, so this tab's UI and
 * cache no longer match the token its requests now carry.
 * @param event the window `storage` event
 * @returns true when the stored token's acting customer changed (token refreshes and logouts don't count)
 */
export const isViewAsChangeFromOtherTab = (event: Pick<StorageEvent, 'key' | 'oldValue' | 'newValue'>): boolean => (
  event.key === TOKEN_STORAGE_KEY
  && Boolean(event.newValue)
  && actingClaimOf(event.oldValue) !== actingClaimOf(event.newValue)
)

export { localizeRoute }

/**
 * Re-mints the session for `customerId` (or the home customer when null), rebuilds the
 * stored profile from whichever session is now stored, then reloads so every cached query
 * and profile reader starts over under that customer.
 * @param customerId the customer to view as, or null to return home
 * @param locale the active i18n language, kept on the reload
 * @throws when the token exchange fails; nothing has changed and the previous session still works
 */
export const switchViewAsCustomer = async (customerId: string | null, locale?: string): Promise<void> => {
  const profile = getUserProfile()
  const userId = profile.userId ?? profile.id
  storeTokens(customerId ? await enterViewAsRequest(customerId) : await exitViewAsRequest())

  try {
    let user = await getUserFromIdRequest(userId)
    if (customerId && user.customer?.id !== customerId) {
      // Customer deleted since the list loaded: the backend fell back to home, so leave view-as too.
      storeTokens(await exitViewAsRequest())
      user = await getUserFromIdRequest(userId)
    }
    const nextProfile = {
      ...profile,
      ...user,
      // Cleared on exit: the profile from the previous view-as would otherwise keep it.
      homeCustomer: user.homeCustomer ?? null,
      teamName: user.customer.name,
      customerId: user.customer.id,
    }
    storeWorkflowSummary({ workflows: user.customer.workflows })
    storeUserProfile(nextProfile)
    CUSTOMER_SCOPED_FILTERS.forEach(key => localStorage.removeItem(key))
    window.location.assign(localizeRoute(getLandingRouteForProfile(nextProfile), locale))
  } catch (error) {
    // The exchange closed the previous token pair, so there is no session to fall back to;
    // signing out beats leaving the token and profile on different customers.
    Sentry.captureException(error)
    await forceLogout()
  }
}
